/**
 * CollabBadge — drop-in "🟢 LIVE · 3 peers · 🔗 Share" pill.
 *
 * Usage (DeskeyPaint / DeskeyChart):
 *   const { peerCount, status } = useCanvasCollab(roomId, { state, apply });
 *   <CollabBadge roomId={roomId} peerCount={peerCount} status={status} />
 *
 * The Share button copies the /live/<roomId> link (lib/collabRoom). Opening
 * it on another device, from any page, opens the same canvas in the room.
 */
import React, { useState } from 'react';
import { Users, Link2, Check, WifiOff } from 'lucide-react';
import { toast } from 'sonner';
import { liveLinkFor } from '../lib/collabRoom';

export default function CollabBadge({ roomId, peerCount = 0, status = 'idle', accent = '#10B981', className = '' }) {
  const [copied, setCopied] = useState(false);
  if (!roomId) return null;

  const copyLink = async () => {
    const url = liveLinkFor(roomId);
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success('Live collab link copied!');
      setTimeout(() => setCopied(false), 1800);
    } catch (_e) {
      // Fallback: prompt
      window.prompt('Copy this link', url);
    }
  };

  const live = status === 'open';
  const dotColor = live ? accent : '#9CA3AF';

  return (
    <div
      className={`inline-flex items-center gap-1.5 rounded-full px-2 py-1 text-[10px] font-bold border ${className}`}
      data-testid="collab-badge"
      style={{
        background: live ? 'color-mix(in srgb, ' + accent + ' 12%, transparent)' : 'transparent',
        borderColor: live ? accent : '#e5e7eb',
        color: live ? accent : '#475569',
      }}>
      <span className="inline-block w-1.5 h-1.5 rounded-full" style={{
        background: dotColor,
        boxShadow: live ? `0 0 6px ${dotColor}` : 'none',
        animation: live ? 'pulse 1.6s infinite' : 'none',
      }} />
      {live ? 'LIVE' : (status === 'connecting' ? 'CONNECTING…' : <><WifiOff className="w-2.5 h-2.5 inline mr-0.5" /> OFFLINE</>)}
      <span className="opacity-60">·</span>
      <span className="inline-flex items-center gap-0.5"><Users className="w-2.5 h-2.5" />{peerCount}</span>
      <button
        type="button"
        onClick={copyLink}
        data-testid="collab-share-link"
        title="Copy live link"
        className="ml-1 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded hover:bg-black/5">
        {copied ? <Check className="w-2.5 h-2.5" /> : <Link2 className="w-2.5 h-2.5" />}
        <span>{copied ? 'Copied' : 'Share'}</span>
      </button>
    </div>
  );
}
